/* eslint-disable @typescript-eslint/no-explicit-any */
import { GridPaginationModel, GridSortItem, GridSortModel } from "@bvhi/ui-base";
import { Route, useNavigate, UseNavigateResult } from "@tanstack/react-router";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SortConfig } from "../views/app/user/users/list/hooks/use-users-sort-params";

interface BaseFilterObject {
  page?: number;
  size?: number;
  sort?: string[];
}

export const useBVHIDataGridFilter = <T extends BaseFilterObject>(
  resetValues: T,
  routeFullPath?: Route["fullPath"],
  getSortConfig?: (sortModel?: GridSortItem) => SortConfig,
  initialValues?: T
) => {
  const [requestParams, setRequestParams] = useState<T>(initialValues ? initialValues : ({} as T));
  const navigate = useNavigate({ from: routeFullPath });

  const updateSearchParamsDebounced = useMemo(
    () =>
      debounce(
        (navigateFunc: UseNavigateResult<"/" | `/${any}/`>, params: T) => {
          void navigateFunc({
            search: { ...params },
            replace: true,
          });
        },
        500,
        { leading: false }
      ),
    []
  );

  useEffect(() => {
    if (!routeFullPath) return;
    updateSearchParamsDebounced(navigate, requestParams);
  }, [requestParams, navigate, updateSearchParamsDebounced, routeFullPath]);

  const handleDataGridChange = useCallback(
    (paginationModel: GridPaginationModel, sortModel: GridSortModel) => {
      let sortConfig: SortConfig | undefined;

      if (getSortConfig) {
        const firstSortItem = sortModel[0];
        sortConfig = getSortConfig(firstSortItem);
      }

      setRequestParams((prevState) => ({
        ...prevState,
        page: paginationModel.page,
        size: paginationModel.pageSize,
        ...(sortConfig && { sort: [sortConfig.backendString] }),
      }));
    },

    [getSortConfig, setRequestParams]
  );

  const handleFilterChange = useCallback(
    ({ key, value }: { key: string; value: string | string[] }) => {
      setRequestParams((prevState) => ({
        ...prevState,
        page: 0,
        [key]: value,
      }));
    },
    [setRequestParams]
  );

  const resetFilter = useCallback(() => {
    setRequestParams(resetValues);
  }, [setRequestParams, resetValues]);

  return { resetFilter, handleFilterChange, handleDataGridChange, requestParams };
};
